import React, { FunctionComponent, useEffect } from 'react';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@material-ui/core';
import Snackbar from 'src/components/Snackbar';
import Alert from '@material-ui/lab/Alert';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ReplayIcon from '@material-ui/icons/Replay';
import CopyToClipboard from 'react-copy-to-clipboard';
import ReactMarkdown from 'react-markdown';
import * as markdownUtils from 'src/utils/markdownUtils';
import { copyTextAsync } from 'src/utils/clipboard';
import { logError } from 'src/utils/Error';
import Spinner from 'src/components/Spinner';
import IPipeline from 'src/models/IPipeline';
import useDiagnose, { FixType } from 'src/hooks/useDiagnose';
import history from 'src/utils/history';
import meltyBanner from 'src/assets/images/svgs/melty-banner.svg';
import 'src/assets/css/components/pipelineDiagnoseModal.css';

export interface IPipelineDiagnoseModalProps {
    open: boolean
    onClose: () => void
    pipeline: IPipeline
    workspaceId?: string
    // optional specific run to diagnose (MEL-574 per-run diagnosis, from the run history dropdown);
    // when omitted the pipeline's most recent failure is diagnosed (the pipeline-level button)
    jobId?: string
}

// Product name for the AI diagnostics feature (backend/setting names stay claude.*).
const MELTY = 'Melty AI'
const PRIVACY_DISCLOSURE_URL = 'https://docs.meltano.com/meltano-cloud/pipeline-diagnosis#data-handling-and-privacy'

const FIX_TYPE_META: Record<FixType, { color: string; cta: string }> = {
    PR: { color: '#4caf50', cta: 'Looks fixable via a pull request to the pipeline repository.' },
    SUPPORT: { color: '#ff9800', cta: 'Looks like an infrastructure issue — raise a Meltano support ticket.' },
    INVESTIGATE: { color: '#9e9e9e', cta: 'Needs more investigation — copy the diagnosis and continue in the Claude app.' },
    UNKNOWN: { color: '#9e9e9e', cta: '' },
}

// The steps shown while diagnosing. The backend streams tokens over a single opaque
// POST and does not emit per-step events, so steps 0–3 are advanced on a short timer
// (cosmetic — we can't observe them individually) while step 4 ("Generate diagnosis")
// is driven by the real signal of the first token arriving. See stepIndex logic below.
const DIAGNOSE_STEPS = [
    'Connecting to Claude API',
    'Read pipeline configuration',
    'Inspect recent run logs',
    'Identify error patterns',
    'Generate diagnosis',
]
const GENERATE_STEP = DIAGNOSE_STEPS.length - 1

const errorMessage = (error?: string): string => {
    switch (error) {
        case 'invalid_key':
            return 'Your Claude API key was rejected. Update it in Settings.'
        case 'rate_limit':
            return 'Your Claude token/usage limit was exceeded. Please try again later.'
        case 'timeout':
            return 'The diagnosis timed out. Please try again.'
        case 'model_unavailable':
            return `${MELTY} is temporarily misconfigured (model unavailable). Please contact support.`
        case 'context_too_large':
            return `This pipeline's logs and repository are too large to diagnose automatically. Use “Copy diagnosis prompt” below to run it in your own Claude.`
        case 'provider_error':
            return `${MELTY} couldn't reach Claude. This is usually a temporary provider issue — please try again in a moment. If it keeps happening, re-check your API key in Settings.`
        case 'request_failed':
            return `${MELTY} couldn't complete the request. Please try again.`
        default:
            return 'The diagnosis service failed. Please try again.'
    }
}

//
// Modal that runs and renders an AI diagnosis of a failed pipeline run (MEL-574,
// "Melty AI"). Drives useDiagnose and renders the no-key promo / diagnosing (stepped)
// / done ("Pipeline diagnosed" + copy + re-run) / error / no-failure states.
//
const PipelineDiagnoseModal: FunctionComponent<IPipelineDiagnoseModalProps> = ({
    open,
    onClose,
    pipeline,
    workspaceId,
    jobId,
}) => {
    const { status, text, fixType, error, contextBundle, diagnose, fetchContext, reset } = useDiagnose()
    // null = no toast; otherwise the message to show (distinct per copy action, MEL-574 #2)
    const [copyMessage, setCopyMessage] = React.useState<string | null>(null)
    const [stepIndex, setStepIndex] = React.useState(0)
    // true while a copy click is waiting on the /diagnose/context fetch — drives an in-button
    // "Preparing…" spinner so a cold first click is never a silent multi-second wait (e2e feedback)
    const [isCopyingPrompt, setCopyingPrompt] = React.useState(false)

    useEffect(() => {
        // Warm the ~1.1 MB promo banner as soon as the modal opens, so if this turns out to be the
        // no-key state the image is already fetched/decoded and paints together with the promo body
        // rather than popping in a second later (MEL-574 popup polish). Harmless when a key is set —
        // the browser just holds a cached asset that's never shown.
        if (open) {
            const img = new Image()
            img.src = meltyBanner
        }
    }, [open])

    useEffect(() => {
        // keyed on workspaceId/pipeline.id too: if the modal opens before the workspace has
        // resolved (workspaceId undefined), this re-fires once it does rather than leaving the
        // modal stuck on the blank idle state
        if (open && workspaceId) {
            diagnose(workspaceId, pipeline.id, false, jobId)
            // fetch the real input context in parallel — independent of the stream / key, so the
            // "Copy diagnosis prompt" button works in the done AND no-key states (MEL-574 feedback)
            fetchContext(workspaceId, pipeline.id, jobId)
        }
        // NB: resetting on !open here would swap the body to the idle/promo state *during* the
        // dialog's close animation, flashing a second popup for a few ms. Reset is deferred to the
        // Dialog's onExited transition hook instead, so it only fires once the modal is gone.
    }, [open, workspaceId, pipeline.id, jobId])

    // Step progression. The backend streams over one opaque POST with only two real signals we
    // can observe — the request being in-flight, and the first token arriving — so the pre-generate
    // steps (0–3) are paced on a timer while we wait. Two deliberate honesty choices vs. the old
    // 700ms flash: (1) the cadence is slow enough (~1.6s) that the steps read as work in progress
    // rather than instantly-green theatre, and (2) the checklist parks on the last pre-generate
    // step ("Identify error patterns", index GENERATE_STEP-1) and only advances to "Generate
    // diagnosis" when the first real token arrives — after which the live text streams in below,
    // so the final step is driven by, and shows, genuine output. Reset when not streaming.
    useEffect(() => {
        if (status !== 'streaming') {
            setStepIndex(0)
            return
        }
        if (text) {
            setStepIndex(GENERATE_STEP)
            return
        }
        const id = setInterval(
            () => setStepIndex(i => (i < GENERATE_STEP - 1 ? i + 1 : i)),
            1600,
        )
        return () => clearInterval(id)
    }, [status, text])

    const meta = fixType ? FIX_TYPE_META[fixType] : undefined
    const copyText = text
    // The real input context assembled server-side (system grounding + failure context + repo),
    // i.e. the actual prompt sent to Claude — for "Copy diagnosis prompt", so a user can run the
    // same diagnosis in their own Claude / locally. NOT a wrapper around the output (MEL-574).
    const promptText = contextBundle?.prompt || ''
    const hasPrompt = !!contextBundle?.prompt

    const handleAddCredential = () => {
        onClose()
        if (workspaceId)
            history.push(`/lab/${workspaceId}/settings`)
    }

    const handleReRun = () => {
        if (workspaceId)
            diagnose(workspaceId, pipeline.id, true, jobId)
    }

    // Copies the diagnosis prompt. If the context bundle is already loaded we copy it directly;
    // otherwise we kick off the fetch and hand the pending prompt straight to the clipboard within
    // this click gesture (see copyTextAsync) — so the button never needs to be disabled while the
    // /diagnose/context request is still in flight (MEL-574 e2e feedback).
    const handleCopyPrompt = () => {
        const ready = hasPrompt
        const pending = ready
            ? Promise.resolve(promptText)
            : workspaceId
                ? fetchContext(workspaceId, pipeline.id, jobId).then(p => p || '')
                : Promise.resolve('')
        // only show the in-button spinner when the prompt isn't already loaded (avoids a flash)
        if (!ready) setCopyingPrompt(true)
        copyTextAsync(pending)
            .then(() => setCopyMessage('Prompt copied to clipboard'))
            .catch(logError)
            .finally(() => setCopyingPrompt(false))
    }

    // label + spinner shown on every "Copy diagnosis prompt" button while a cold fetch is in flight
    const copyPromptLabel = isCopyingPrompt
        ? <><Spinner active /><span style={{ marginLeft: 8 }}>Preparing prompt…</span></>
        : 'Copy diagnosis prompt'

    const renderBody = () => {
        if (status === 'no-key')
            return (
                <div data-testid="melty-promo" style={{ position: 'relative' }}>
                    {/* floating close — sits over the banner, top-right, no top gap (MEL-574 UI feedback) */}
                    <IconButton
                        aria-label="Close"
                        size="small"
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            color: '#fff',
                            backgroundColor: 'rgba(0, 0, 0, 0.25)',
                        }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <img
                        data-testid="melty-promo-banner"
                        src={meltyBanner}
                        alt={MELTY}
                        style={{ display: 'block', width: '100%', height: 'auto' }} />
                    <div style={{ padding: '16px 24px' }}>
                        <h2 style={{ margin: '0 0 12px', fontSize: 20 }}>Introducing {MELTY}</h2>
                        <p style={{ margin: '0 0 16px', lineHeight: 1.5, color: '#444' }}>
                            When a pipeline fails silently, {MELTY} reads the failure logs, recent run history,
                            and pipeline code to figure out what went wrong, then gives you an explanation and
                            steps to fix it. This runs on your own Claude API key. Add one to get started, it
                            only takes a minute.
                        </p>
                        <p style={{ margin: 0, fontSize: 12, color: '#888', lineHeight: 1.5 }}>
                            {MELTY} sends failure logs, recent run history, and pipeline code and configurations
                            (including plugins and triggers).{' '}
                            <a
                                data-testid="melty-disclosure-link"
                                href={PRIVACY_DISCLOSURE_URL}
                                target="_blank"
                                rel="noreferrer">
                                Read the full disclosure here
                            </a>.
                        </p>
                    </div>
                </div>
            )

        if (status === 'no-failure')
            return <Alert severity="success">{text || 'No recent failed run to diagnose.'}</Alert>

        if (status === 'error')
            return <Alert severity="error">{errorMessage(error)}</Alert>

        if (status === 'streaming')
            return (
                <div data-testid="melty-diagnosing">
                    <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {DIAGNOSE_STEPS.map((label, i) => {
                            const done = i < stepIndex
                            const active = i === stepIndex
                            return (
                                <li
                                    key={label}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '6px 0',
                                        color: done ? '#4caf50' : active ? '#111' : '#bbb',
                                    }}>
                                    {done ? (
                                        <CheckCircleIcon fontSize="small" style={{ color: '#4caf50' }} />
                                    ) : active ? (
                                        <Spinner active />
                                    ) : (
                                        <RadioButtonUncheckedIcon fontSize="small" style={{ color: '#ccc' }} />
                                    )}
                                    <span>{label}{active ? '..' : ''}</span>
                                </li>
                            )
                        })}
                    </ol>
                    {stepIndex === GENERATE_STEP && text && (
                        // real output as it streams — the genuine per-token signal, not a timer
                        <div
                            data-testid="melty-live-text"
                            style={{
                                marginTop: 12,
                                padding: 12,
                                borderRadius: 4,
                                backgroundColor: '#fafafa',
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.5,
                                fontSize: 13,
                                color: '#444',
                                maxHeight: 220,
                                overflowY: 'auto',
                            }}>
                            {text}
                        </div>
                    )}
                </div>
            )

        if (status === 'idle')
            // request in flight (or not yet started) — a plain spinner, never the empty
            // diagnosed view that used to flash here before the promo (MEL-574 #5)
            return (
                <div
                    data-testid="melty-loading"
                    style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                    <Spinner active />
                </div>
            )

        // status === 'done'
        return (
            <div data-testid="melty-diagnosed">
                <div className="melty-markdown" style={{ lineHeight: 1.5 }}>
                    <ReactMarkdown renderers={markdownUtils.renderers} escapeHtml={false} source={text} />
                </div>
                {meta && meta.cta && (
                    <div style={{ marginTop: 16 }}>
                        <Chip
                            size="small"
                            label={fixType}
                            style={{ backgroundColor: meta.color, color: '#fff', marginBottom: 8 }} />
                        <p style={{ margin: 0, color: '#555' }}>{meta.cta}</p>
                    </div>
                )}
                <div style={{ marginTop: 16 }}>
                    <a
                        data-testid="melty-rerun"
                        role="button"
                        onClick={handleReRun}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            color: '#7C56DC',
                            cursor: 'pointer',
                            fontSize: 13,
                            textDecoration: 'underline',
                        }}>
                        <ReplayIcon fontSize="small" />
                        Diagnosis unhelpful? Re-run diagnosis
                    </a>
                </div>
            </div>
        )
    }

    const title =
        status === 'streaming' ? 'Diagnosing your pipeline..'
            : status === 'done' ? 'Pipeline diagnosed'
                : status === 'no-key' ? ''
                    : pipeline.label

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            TransitionProps={{ onExited: reset }}
            PaperProps={{
                // 8px radius everywhere; overflow:hidden on the no-key state so the paper's rounded
                // corners cleanly clip the full-bleed (square) banner image with no gaps
                style: status === 'no-key'
                    ? { overflow: 'hidden', borderRadius: 8 }
                    : { borderRadius: 8 },
            }}>
            {title && (
                <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
                    <IconButton aria-label="Close" size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
            )}
            <DialogContent
                dividers={status !== 'no-key'}
                style={status === 'no-key' ? { padding: 0 } : undefined}>
                {renderBody()}
            </DialogContent>

            {status === 'no-key' && (
                <DialogActions>
                    {/* keyless path: copy the real input prompt without configuring a Claude key.
                        Rendered while the context GET is still in flight (contextBundle undefined) so
                        it appears together with the other action instead of popping in a few seconds
                        later — disabled until the prompt loads. Only hidden once we definitively know
                        there is no failure to build a prompt from. */}
                    {contextBundle?.hasFailure !== false && (
                        <Button
                            data-testid="melty-copy-prompt-nokey"
                            variant="outlined"
                            onClick={handleCopyPrompt}
                            disabled={isCopyingPrompt}
                            style={{ color: '#7C56DC', borderColor: '#7C56DC', textTransform: 'none' }}>
                            {copyPromptLabel}
                        </Button>
                    )}
                    <Button
                        data-testid="melty-add-credential"
                        variant="contained"
                        disableElevation
                        onClick={handleAddCredential}
                        style={{ backgroundColor: '#7C56DC', color: '#fff', textTransform: 'none' }}>
                        Add Claude Credential
                    </Button>
                </DialogActions>
            )}

            {status === 'error' && contextBundle?.hasFailure && (
                // any failure (esp. context_too_large) still lets the user grab the real prompt to
                // debug locally — the fallback the error copy points at (MEL-600)
                <DialogActions>
                    <Button
                        data-testid="melty-copy-prompt-error"
                        variant="contained"
                        disableElevation
                        onClick={handleCopyPrompt}
                        disabled={isCopyingPrompt}
                        style={{ backgroundColor: '#7C56DC', color: '#fff', textTransform: 'none' }}>
                        {copyPromptLabel}
                    </Button>
                </DialogActions>
            )}

            {status === 'done' && (
                <DialogActions>
                    <Button
                        data-testid="melty-copy-prompt"
                        variant="outlined"
                        onClick={handleCopyPrompt}
                        disabled={isCopyingPrompt}
                        style={{ color: '#7C56DC', borderColor: '#7C56DC', textTransform: 'none' }}>
                        {copyPromptLabel}
                    </Button>
                    <CopyToClipboard text={copyText} onCopy={() => setCopyMessage('Copied to clipboard')}>
                        <Button
                            data-testid="melty-copy-clipboard"
                            variant="contained"
                            disableElevation
                            style={{ backgroundColor: '#7C56DC', color: '#fff', textTransform: 'none' }}>
                            Copy to clipboard
                        </Button>
                    </CopyToClipboard>
                </DialogActions>
            )}

            <Snackbar
                open={!!copyMessage}
                // bottom-right to match the run-history dropdown's copy snackbar (DataRow), so the
                // confirmation appears in the same place from both "Copy diagnosis prompt" entry points
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                onClose={() => setCopyMessage(null)}
                message={copyMessage} />
        </Dialog>
    )
}

export default PipelineDiagnoseModal
